/**
 * SortableList — accessible drag-to-reorder primitive (headless).
 *
 * Behavior-only. Renders the list container and per-item wiring; the
 * consumer styles rows and supplies the drag handle (typically a grip icon
 * button). Drag is **handle-only** — the row itself is not draggable, only
 * the element receiving `handleProps`.
 *
 * Built on `@dnd-kit/core` + `@dnd-kit/sortable`. Keyboard reorder, screen-
 * reader announcements, pointer activation constraints, and reduced-motion
 * respect come from dnd-kit out of the box. Native HTML5 drag is not
 * keyboard-accessible; this primitive replaces it everywhere in the Tensaw
 * surface area.
 *
 * Controlled: caller owns `items` and applies `onReorder(nextItems)` to
 * state. The reorder semantic is "new array in display order" — useful
 * when array order itself is meaningful (e.g., execution order of steps
 * in a chain).
 *
 * Usage:
 *
 *   <SortableList
 *     items={steps}
 *     getItemId={(s) => s.id}
 *     onReorder={setSteps}
 *     aria-label="Cleaning steps, in order"
 *   >
 *     {(step, { setNodeRef, style, handleProps, isDragging }) => (
 *       <li ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''}>
 *         <button type="button" aria-label={`Reorder: ${step.title}`} {...handleProps}>
 *           <GripVertical size={16} aria-hidden="true" />
 *         </button>
 *         {step.title}
 *       </li>
 *     )}
 *   </SortableList>
 *
 * Implementation notes:
 *   - `handleProps` includes a `ref` callback (dnd-kit's `setActivatorNodeRef`)
 *     so the consumer's `<button {...handleProps}>` wires the activator
 *     correctly. React 18 spreads `ref` onto HTML elements normally.
 *   - `disabledIds` items are still rendered, but their useSortable hook
 *     is initialized with `disabled: true` — they neither initiate drag
 *     nor get displaced.
 *   - `orientation` selects the matching strategy (vertical / horizontal)
 *     and the matching axis-restriction modifier.
 */
import {
  Children,
  forwardRef,
  type CSSProperties,
  type JSX,
  type ReactNode,
} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type Announcements,
  type ScreenReaderInstructions,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

import { cn } from '../../utils/cn';

// -- Public types -------------------------------------------------------------

export interface SortableRenderArgs {
  /** Ref for the draggable row root. */
  setNodeRef: (el: HTMLElement | null) => void;
  /** Transform/transition style for the row while dragging/animating. */
  style: CSSProperties;
  /**
   * Spread onto your drag-handle (grip) element. Wires pointer + keyboard
   * drag, ARIA attributes, and the activator ref. Handle-only dragging.
   *
   * Includes a `ref` callback; React 18 spreads it onto HTML elements
   * correctly.
   */
  handleProps: Record<string, unknown>;
  /** True while this row is the active drag item. */
  isDragging: boolean;
  /** Stable id derived from `getItemId(item)`. */
  id: string;
  /** Zero-based index in the current display order. */
  index: number;
}

export interface SortableListProps<T> {
  items: T[];
  getItemId: (item: T) => string;
  /**
   * Called with the FULL reordered array on drop. Controlled — caller
   * owns state. The array is in display order.
   */
  onReorder: (nextItems: T[]) => void;
  /** Default 'vertical'. */
  orientation?: 'vertical' | 'horizontal';
  /** Ids that cannot be moved and cannot be displaced. */
  disabledIds?: string[];
  /**
   * Render one row. Spread `handleProps` on the grip element;
   * apply `setNodeRef` + `style` to the row root.
   */
  children: (item: T, args: SortableRenderArgs) => ReactNode;
  /** Accessible name for the list region. */
  'aria-label'?: string;
  /** Optional override of the default screen-reader announcements. */
  announcements?: Partial<Announcements>;
  /** Optional override of the default screen-reader instructions. */
  screenReaderInstructions?: ScreenReaderInstructions;
  /** Element/tag for the list container (default 'ul'). */
  as?: keyof JSX.IntrinsicElements;
  className?: string;
}

// -- Defaults -----------------------------------------------------------------

const DEFAULT_INSTRUCTIONS: ScreenReaderInstructions = {
  draggable:
    'To pick up a sortable item, press space or enter. ' +
    'Use the arrow keys to move the item. ' +
    'Press space or enter again to drop the item in its new position, ' +
    'or press escape to cancel.',
};

function defaultAnnouncements(getLabel?: (id: string) => string): Announcements {
  const labelFor = (id: string | number | undefined) =>
    id === undefined ? 'item' : (getLabel?.(String(id)) ?? `item ${String(id)}`);

  return {
    onDragStart({ active }) {
      return `Picked up ${labelFor(active.id)}.`;
    },
    onDragOver({ active, over }) {
      if (over) {
        return `${labelFor(active.id)} was moved over ${labelFor(over.id)}.`;
      }
      return `${labelFor(active.id)} is no longer over a droppable area.`;
    },
    onDragEnd({ active, over }) {
      if (over) {
        return `${labelFor(active.id)} was dropped over ${labelFor(over.id)}.`;
      }
      return `${labelFor(active.id)} was dropped.`;
    },
    onDragCancel({ active }) {
      return `Sorting was cancelled. ${labelFor(active.id)} was returned to its starting position.`;
    },
  };
}

// -- Item subcomponent --------------------------------------------------------

interface SortableItemProps<T> {
  item: T;
  id: string;
  index: number;
  disabled: boolean;
  children: (item: T, args: SortableRenderArgs) => ReactNode;
}

function SortableItem<T>({
  item,
  id,
  index,
  disabled,
  children,
}: SortableItemProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // dnd-kit suggests these for clean drag stacking:
    zIndex: isDragging ? 1 : undefined,
    opacity: isDragging ? 0.85 : undefined,
  };

  const handleProps: Record<string, unknown> = {
    ...attributes,
    ...listeners,
    ref: setActivatorNodeRef,
    // When disabled, the consumer's grip button is still rendered (so the
    // row reads correctly), but no listeners are attached and aria-disabled
    // is set so AT users understand the affordance is off.
    'aria-disabled': disabled || undefined,
  };

  return <>{children(item, { setNodeRef, style, handleProps, isDragging, id, index })}</>;
}

// -- Root component -----------------------------------------------------------

function SortableListInner<T>(
  {
    items,
    getItemId,
    onReorder,
    orientation = 'vertical',
    disabledIds,
    children,
    announcements,
    screenReaderInstructions,
    as,
    className,
    ...rest
  }: SortableListProps<T>,
  ref: React.Ref<HTMLElement>,
) {
  const Tag = (as ?? 'ul') as keyof JSX.IntrinsicElements;
  const ariaLabel = (rest as { 'aria-label'?: string })['aria-label'];

  // Compute the ordered id list once per render. `noUncheckedIndexedAccess`
  // guard: we only index after a defined-length iteration.
  const ids: string[] = items.map((it) => getItemId(it));
  const disabled = new Set(disabledIds ?? []);

  // Build a lookup: id → original item, so DragEnd can produce the
  // reordered array of T (not just ids).
  const itemsById = new Map<string, T>();
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it === undefined) continue;
    itemsById.set(ids[i] ?? '', it);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Don't start a drag on a plain click on the handle; require a small
      // movement first. 5px matches dnd-kit's recommended default for
      // mixed click/drag affordances.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;

    // Don't move a disabled item, and don't move into a disabled-target
    // displacement. (dnd-kit's `disabled` already prevents disabled items
    // from being dragged or displaced, but we also guard here for safety.)
    if (disabled.has(String(active.id)) || disabled.has(String(over.id))) {
      return;
    }

    const nextItems = arrayMove(items, oldIndex, newIndex);
    onReorder(nextItems);
  };

  const strategy =
    orientation === 'horizontal'
      ? horizontalListSortingStrategy
      : verticalListSortingStrategy;

  const modifiers =
    orientation === 'horizontal'
      ? [restrictToHorizontalAxis, restrictToParentElement]
      : [restrictToVerticalAxis, restrictToParentElement];

  const mergedAnnouncements: Announcements = {
    ...defaultAnnouncements(),
    ...(announcements ?? {}),
  };

  // The list container. Headless: no colors, no padding, no spacing.
  // When `as` is `ul` or `ol`, inline the minimal reset (no list-style,
  // no UA margin/padding) so consumers don't fight default styles. Inline
  // because we don't want consumers to need a CSS import.
  const containerStyle: CSSProperties | undefined =
    Tag === 'ul' || Tag === 'ol'
      ? { listStyle: 'none', margin: 0, padding: 0 }
      : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements: mergedAnnouncements,
        screenReaderInstructions:
          screenReaderInstructions ?? DEFAULT_INSTRUCTIONS,
      }}
    >
      <SortableContext items={ids} strategy={strategy}>
        {/*
         * Render-prop loop. We can't pass `ref` through a polymorphic
         * intrinsic element with a generic type cleanly, so the container
         * is a plain JSX element; consumers who need a ref to the container
         * can wrap.
         */}
        {(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const Element: any = Tag;
          return (
            <Element
              ref={ref}
              role="list"
              aria-label={ariaLabel}
              className={cn(className)}
              style={containerStyle}
              data-orientation={orientation}
            >
              {items.map((item, index) => {
                const id = ids[index];
                if (id === undefined) return null;
                return (
                  <SortableItem
                    key={id}
                    item={item}
                    id={id}
                    index={index}
                    disabled={disabled.has(id)}
                  >
                    {children}
                  </SortableItem>
                );
              })}
            </Element>
          );
        })()}
      </SortableContext>
    </DndContext>
  );
}

/**
 * The forwardRef + generic dance: we need a generic over T AND a
 * forwarded ref. The standard idiom is `forwardRef<HTMLElement, ...>(
 * Inner) as <T>(...) => …`.
 */
export const SortableList = forwardRef(SortableListInner) as <T>(
  props: SortableListProps<T> & { ref?: React.Ref<HTMLElement> },
) => ReturnType<typeof SortableListInner>;

// Set a displayName via a type assertion (forwardRef + generic loses it).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(SortableList as any).displayName = 'SortableList';

// Re-export the dnd-kit type so consumers don't have to import it
// directly. `Announcements` is on the SortableListProps signature.
export type { Announcements, ScreenReaderInstructions } from '@dnd-kit/core';
